// frontend/src/services/authService.js - CORRECTED VERSION
import api from './api';

const authService = {
  login: async (username, password) => {
    try {
      // CORRECTED: Use correct Django SimpleJWT endpoint
      const response = await api.post('/auth/login/', { 
        username, 
        password 
      });

      console.log('Login response:', response.data);

      if (response.data.access) {
        // Store tokens
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);

        // Also fetch user info
        const userResponse = await api.get('/auth/user/');
        const userData = userResponse.data;

        // Add admin status to user data
        const userWithAdmin = {
          ...userData,
          isAdmin: userData?.is_staff || userData?.is_superuser || false
        };

        localStorage.setItem('user', JSON.stringify(userWithAdmin));

        return {
          ...response.data,
          user: userWithAdmin,
        };
      } else {
        // Handle different response format
        console.warn('Unexpected login response format:', response.data);
        return response.data;
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  register: async (userData) => {
    try {
      const response = await api.post('/auth/register/', userData);
      console.log('Register response:', response.data);

      // Handle different registration response formats
      if (response.data.access) {
        // Format 1: Direct tokens in response
        localStorage.setItem('access_token', response.data.access);
        localStorage.setItem('refresh_token', response.data.refresh);

        if (response.data.user) {
          const userWithAdmin = {
            ...response.data.user,
            isAdmin: response.data.user?.is_staff || response.data.user?.is_superuser || false
          };
          localStorage.setItem('user', JSON.stringify(userWithAdmin));
        } else {
          // Fetch user info
          const userResponse = await api.get('/auth/user/');
          const userWithAdmin = {
            ...userResponse.data,
            isAdmin: userResponse.data?.is_staff || userResponse.data?.is_superuser || false
          };
          localStorage.setItem('user', JSON.stringify(userWithAdmin));
        }
      } else if (response.data.tokens?.access) {
        // Format 2: Nested tokens
        localStorage.setItem('access_token', response.data.tokens.access);
        localStorage.setItem('refresh_token', response.data.tokens.refresh);

        if (response.data.user) {
          const userWithAdmin = {
            ...response.data.user,
            isAdmin: response.data.user?.is_staff || response.data.user?.is_superuser || false
          };
          localStorage.setItem('user', JSON.stringify(userWithAdmin));
        }
      } else if (response.data.detail) {
        // Format 3: Registration success without auto-login
        console.log('Registration successful, no auto-login:', response.data.detail);
      }

      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  logout: () => {
    try {
      // Call logout API if exists
      api.post('/auth/logout/').catch(() => {
        // Ignore errors if logout endpoint doesn't exist
      });
    } catch (error) {
      console.log('Logout API call failed, clearing local storage only');
    }
    
    // Clear local storage
    if (api.clearAuth) {
      api.clearAuth();
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    
    // Redirect to login
    window.location.href = '/login';
  },

  getProfile: async () => {
    try {
      const response = await api.get('/auth/user/');
      const userData = response.data;

      // Add admin status to user data
      const userWithAdmin = {
        ...userData,
        isAdmin: userData?.is_staff || userData?.is_superuser || false
      };

      // Update localStorage
      localStorage.setItem('user', JSON.stringify(userWithAdmin));

      return userWithAdmin;
    } catch (error) {
      console.error('Get profile error:', error);
      
      // If unauthorized, clear auth and redirect
      if (error.response?.status === 401) {
        authService.logout();
      }
      throw error;
    }
  },

  getCurrentUser: () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;

      const userData = JSON.parse(userStr);

      // Ensure isAdmin property exists
      if (userData && !userData.hasOwnProperty('isAdmin')) {
        userData.isAdmin = userData?.is_staff || userData?.is_superuser || false;
        localStorage.setItem('user', JSON.stringify(userData));
      }

      return userData;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  },

  getToken: () => {
    return localStorage.getItem('access_token') || localStorage.getItem('token');
  },

  isAuthenticated: () => {
    const token = localStorage.getItem('access_token');
    return !!token;
  },

  isAdmin: () => {
    const user = authService.getCurrentUser();
    return user?.is_staff || user?.is_superuser || user?.isAdmin || false;
  },

  updateProfile: async (profileData) => {
    try {
      // Try multiple profile update endpoints
      let response;
      try {
        response = await api.put('/auth/profile/update/', profileData);
      } catch (error) {
        // Fallback to user endpoint
        response = await api.put('/auth/user/', profileData);
      }

      // Update stored user data
      const currentUser = authService.getCurrentUser() || {};
      const updatedUser = {
        ...currentUser,
        ...response.data,
        // Maintain admin status
        isAdmin: currentUser?.isAdmin || currentUser?.is_staff || currentUser?.is_superuser || false
      };

      localStorage.setItem('user', JSON.stringify(updatedUser));

      return updatedUser;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  },

  changePassword: async (passwordData) => {
    try {
      const response = await api.post('/auth/change-password/', passwordData);
      return response.data;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  },

  verifyEmail: async (token) => {
    try {
      // Use GET request with token in URL (matches Django backend)
      const response = await api.get(`/auth/verify-email/${token}/`);
      return response.data;
    } catch (error) {
      console.error('Verify email error:', error);
      throw error;
    }
  },

  requestPasswordReset: async (email) => {
    try {
      const response = await api.post('/auth/password-reset/', { email });
      return response.data;
    } catch (error) {
      console.error('Request password reset error:', error);
      throw error;
    }
  },

  resetPassword: async (token, password) => {
    try {
      const response = await api.post('/auth/password-reset/confirm/', {
        token,
        password,
      });
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  },

  // Check if token is valid
  checkAuth: async () => {
    try {
      await api.get('/auth/user/');
      return true;
    } catch (error) {
      if (error.response?.status === 401) {
        // Token is invalid or expired
        return false;
      }
      // Other errors (network, server, etc.)
      console.error('Auth check error:', error);
      return false;
    }
  },

  // Refresh access token
  refreshToken: async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post('/auth/token/refresh/', {
        refresh: refreshToken,
      });

      if (response.data.access) {
        localStorage.setItem('access_token', response.data.access);
        return response.data.access;
      }
      
      throw new Error('No access token in refresh response');
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  },

  // Upload profile picture
  uploadProfilePicture: async (file) => {
    try {
      const formData = new FormData();
      formData.append('profile_picture', file);

      const response = await api.post('/auth/upload-profile-picture/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update user in localStorage
      const currentUser = authService.getCurrentUser();
      if (currentUser && response.data.profile_picture_url) {
        currentUser.profile_picture_url = response.data.profile_picture_url;
        localStorage.setItem('user', JSON.stringify(currentUser));
      }

      return response.data;
    } catch (error) {
      console.error('Upload profile picture error:', error);
      throw error;
    }
  },

  // Delete profile picture
  deleteProfilePicture: async () => {
    try {
      const response = await api.delete('/auth/delete-profile-picture/');
      
      // Update user in localStorage
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        currentUser.profile_picture_url = null;
        localStorage.setItem('user', JSON.stringify(currentUser));
      }

      return response.data;
    } catch (error) {
      console.error('Delete profile picture error:', error);
      throw error;
    }
  },

  // Get CSRF token (for forms that need it)
  getCsrfToken: async () => {
    try {
      if (api.getCsrfToken) {
        return await api.getCsrfToken();
      }
      return null;
    } catch (error) {
      console.error('Get CSRF token error:', error);
      return null;
    }
  },

  // Check auth status with auto-refresh
  checkAuthWithRefresh: async () => {
    try {
      // First try normal auth check
      const isAuth = await authService.checkAuth();
      if (isAuth) {
        return true;
      }

      // If not authenticated, try to refresh token
      try {
        await authService.refreshToken();
        return true;
      } catch (refreshError) {
        console.log('Token refresh failed, user needs to login');
        return false;
      }
    } catch (error) {
      console.error('Auth check with refresh error:', error);
      return false;
    }
  },
};

export default authService;
